import React from 'react';
import clsx from 'clsx';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import NumArrow from '@/pages/intelligence/pages/TopCharts/components/DetailsPage/NumArrow';
import { indicatorValRankingFormat } from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overview/PerformanceSummary/cardUtils';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { unitConversion } from '@/pages/dashboard/utils/utils';
import { getCardChart, xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import { yAxisFormat } from '@/pages/dashboard/components/Chart/_config/utils';
import { formatMainValue } from '@sg/pages/StudioManagement/LiveTitles/pages/LiveVisualizationAll/utils';
import { formatLiveTitleCurrencyText, mapUnitToCurrencyCode } from '@sg/pages/StudioManagement/LiveTitles/utils/currencyRules';
import './index.scss';

export interface ILiveMetricBoardTrendData {
  xAxisData: string[];
  series: any[];
  metrics_name: string;
}

interface ILiveMetricBoardProps {
  title: string;
  num?: number | null;
  formatter?: string;
  unit?: string;
  chartHeight?: number;
  textList: Array<{ value: string; text: string }>;
  trendData?: ILiveMetricBoardTrendData;
  extraLines?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}



const getNumConfig = (text: string) => {
  if (!text) return null;
  const {
    _indicatorContent,
    _indicatorSize,
    _indicatorColor,
    _isIndicatorArrow,
    _indicatprsArrowColor,
  } = indicatorValRankingFormat(text);

  return {
    numContent: _indicatorContent,
    numColor: _indicatorColor,
    numSize: _indicatorSize,
    arrow: _isIndicatorArrow,
    arrowColor: _indicatprsArrowColor,
  };
};

const LiveMetricBoard: React.FC<ILiveMetricBoardProps> = ({
  title,
  num,
  formatter,
  unit,
  chartHeight = 130,
  textList,
  trendData,
  extraLines,
  className,
  style,
}) => {
  // 当某条 series 只有一个有效数据点时，折线无法形成，需要强制显示圆点
  // 否则 showSymbol: false 会让单点数据在图中完全不可见
  const normalizedSeries = React.useMemo<any[]>(() => {
    const originSeries = trendData?.series;
    if (!Array.isArray(originSeries)) return [];

    const isValidPoint = (v: any) => {
      if (v === null || v === undefined) return false;
      const raw = typeof v === 'object' ? v?.value : v;
      if (raw === null || raw === undefined) return false;
      return !Number.isNaN(Number(raw));
    };

    return originSeries.map((item: any) => {
      const data = item?.data;
      if (!Array.isArray(data)) return item;

      const validIndexes: number[] = [];
      data.forEach((point: any, idx: number) => {
        if (isValidPoint(point)) validIndexes.push(idx);
      });
      if (validIndexes.length !== 1) return item;

      const onlyIdx = validIndexes[0];
      const originalPoint = data[onlyIdx];
      const isObjectPoint = originalPoint && typeof originalPoint === 'object';
      const pointItemStyle = isObjectPoint ? originalPoint?.itemStyle || {} : {};
      const normalizedPoint = {
        ...(isObjectPoint ? originalPoint : { value: originalPoint }),
        itemStyle: {
          ...pointItemStyle,
          opacity: 1,
        },
        symbol: 'circle',
        symbolSize: 6,
      };
      const newData = data.slice();
      newData[onlyIdx] = normalizedPoint;

      return {
        ...item,
        showSymbol: true,
        showAllSymbol: true,
        data: newData,
      };
    });
  }, [trendData?.series]);

  // If all timeline points are null/invalid, hide chart panel.
  const hasRenderableSeriesData = React.useMemo(() => {
    const series = trendData?.series;
    if (!Array.isArray(series) || series.length === 0) {
      return false;
    }
    return series.some((item: any) => {
      const data = item?.data;
      if (!Array.isArray(data) || data.length === 0) {
        return false;
      }
      return data.some((point: any) => {
        const raw = typeof point === 'object' ? point?.value : point;
        if (raw === null || raw === undefined) {
          return false;
        }
        return !Number.isNaN(Number(raw));
      });
    });
  }, [trendData?.series]);

  const shouldRenderTrendChart = Boolean(
    trendData &&
    (trendData?.xAxisData?.length ?? 0) > 0 &&
    (trendData?.series?.length ?? 0) > 0 &&
    hasRenderableSeriesData,
  );

  const getMonthLabel = (value: any): string => {
    const rawValue = String(value || '');
    // Prefer parsing YYYY-MM(-DD) formats; fallback keeps legacy values usable.
    const monthMatch = rawValue.match(/^\d{4}[-/](\d{1,2})(?:[-/]\d{1,2})?$/);
    if (monthMatch?.[1]) {
      return monthMatch[1].padStart(2, '0');
    }
    return rawValue.slice(-2);
  };

  const getYearMonthLabel = (value: any): string => {
    const rawValue = String(value || '');
    const yearMonthMatch = rawValue.match(/^(\d{4})[-/](\d{1,2})(?:[-/]\d{1,2})?$/);
    if (yearMonthMatch) {
      const [, year, month] = yearMonthMatch;
      return `${year}-${month.padStart(2, '0')}`;
    }
    return rawValue;
  };

  return (
    <div className={clsx('dbs-lto-metric-board', className)} style={style}>
      {/* <div className="dbs-lto-metric-board__header"> */}
      <div className="dbs-lto-metric-board__title">{title}</div>
      {/* </div> */}

      <div className="dbs-lto-metric-board__body">
        <div className="dbs-lto-metric-board__body-left">
          <span className="dbs-lto-metric-board__num">
            {formatMainValue(num, formatter, unit)}
          </span>
          <div className="dbs-lto-metric-board__labels">
            {(textList || []).map((item, index) => {
              const numArrowData = getNumConfig(item?.text);
              return (
                <div
                  className="dbs-lto-metric-board__label-item"
                  key={`${item?.value}-${index}`}
                >
                  <div className="dbs-lto-metric-board__label-key">
                    {item?.value}
                  </div>
                  {numArrowData ? (
                    <NumArrow dataContent={numArrowData} type="num" />
                  ) : (
                    <span className="dbs-lto-metric-board__label-value">-</span>
                  )}
                </div>
              );
            })}
          </div>
          {extraLines && (
            extraLines
          )}
        </div>

        {shouldRenderTrendChart && (
            <div className="dbs-lto-metric-board__body-right">
              <LineChart
                height={chartHeight}
                xAxisData={trendData?.xAxisData || []}
                series={normalizedSeries}
                isShowLegend={false}
                formatterFunc={(param: string | any[]) => {
                  const paramsArr = Array.isArray(param) ? param : [param];
                  // Tooltip header keeps year-month context while x-axis stays month-only.
                  const date = getYearMonthLabel(paramsArr[0]?.axisValue);
                  let returnStr = `<div class=${'basic-formatter-box'}>
                    <div class=${'basic-formatter-header'}>
                    <p class='basic-formatter-one basic-formatter-title'>${date}</p>
                    <p class='basic-formatter-two'>${title}</p></div>`;
                  for (let i = 0; i < paramsArr.length; i++) {
                    const paramValue = paramsArr[i].value;
                    let formatted: any = null;
                    if (paramValue !== null && paramValue !== undefined) {
                      if (isNaN(paramValue)) {
                        formatted = '-';
                      } else if (formatter) {
                        try {
                          const formatRule = (
                            formatter === 'thousands2Decimal' || formatter === '0,0.00'
                          )
                            ? 'db_bm00'
                            : formatter;
                          formatted = dataFormat({ value: Number(paramValue), formatRule });
                        } catch (e) {
                          formatted = Number(paramValue).toLocaleString();
                        }
                      } else {
                        formatted = Number(paramValue).toLocaleString();
                      }
                    } else {
                      formatted = '-';
                    }

                    if (unit === 'hour') {
                      formatted = `${formatted}H`;
                    } else if (unit === 'USD' || unit === 'RMB') {
                      // mapUnitToCurrencyCode(unit):
                      // input 'USD'/'RMB'/'CNY' -> output normalized code ('USD'|'CNY');
                      // rule: rmb/cny => CNY, otherwise => USD.
                      // formatLiveTitleCurrencyText(valueText, currencyCode):
                      // input formatted number text + code -> output symbol-prefixed text
                      // like $12.3K or ¥12.3K.
                      formatted = formatLiveTitleCurrencyText(
                        formatted,
                        mapUnitToCurrencyCode(unit),
                      );
                    }
                    const radio = paramsArr[i].marker || '';
                    returnStr += `<div class=${'basic-formatter-header'}>
                      <p class=${'basic-formatter-one'}>${radio}&nbsp;${paramsArr[i].seriesName}</p>
                      <p class=${'basic-formatter-two'}>${formatted}</p></div>`;
                  }
                  returnStr += '</div>';
                  return returnStr;
                }}
                setOtherConfigFunc={(opt: any) => {
                  opt = getCardChart(opt, { timeType: 'day' });
                  opt = xAxisHighLight(opt, {
                    lineStyle: {
                      type: 'solid',
                    },
                    formatter: (params: any) => {
                      return getMonthLabel(params?.value);
                    },
                    symbolSize: 8,
                    fontSize: 11,
                  });
                  opt.grid = {
                    x: 6,
                    y: 12,
                    x2: 6,
                    y2: 0,
                    containLabel: true,
                  };
                  const isPercentType = formatter && (
                    formatter.includes('percent') ||
                    formatter.includes('Percent') ||
                    formatter === 'percent_int' ||
                    formatter.startsWith('db_percent') ||
                    formatter.startsWith('db_abs_percent')
                  );

                  if (isPercentType) {
                    opt.yAxis.axisLabel.formatter = (value: number) => yAxisFormat(value, true, 2, true);
                  } else if (unit === 'hour') {
                    opt.yAxis.axisLabel.formatter = (value: any) => {
                      const formatLabel = value ? unitConversion(value, true) : value;
                      return `${formatLabel}H`;
                    };
                  } else {
                    opt.yAxis.axisLabel.formatter = (value: number) => {
                      return value ? unitConversion(value, true) : value;
                    };
                  }
                  opt.xAxis = [{
                    ...(opt.xAxis[0] || {}),
                    boundaryGap: false,
                    axisLabel: {
                      ...(opt.xAxis[0]?.axisLabel || {}),
                      fontSize: 11,
                      color: '#BBBDCB',
                      formatter: (value: string) => {
                        return getMonthLabel(value);
                      },
                    },
                  }];
                  opt.yAxis.axisLabel.fontSize = 11;
                  opt.yAxis.axisLabel.color = '#BBBDCB';
                  opt.tooltip.confine = false;
                  opt.tooltip = {
                    ...opt.tooltip,
                    className: 'dbs-lto-chart-tooltip',
                    appendToBody: true,
                    axisPointer: {
                      ...opt.tooltip?.axisPointer,
                      label: {
                        ...opt.tooltip?.axisPointer?.label,
                        padding: [5, 1, 0, 1],
                        backgroundColor: 'transparent',
                      },
                    },
                  };
                  return opt;
                }}
              />
            </div>
          )}
      </div>
    </div>
  );
};

export default LiveMetricBoard;
